import {
  CATALOGUE,
  CATALOGUE_VERSION,
} from "../data/catalogue";
import type {
  CatalogueItem,
  LocalSession,
  Participant,
  ParticipantAnswer,
  PerspectiveAnswer,
  Seat,
  SharedNote,
  Stance,
  StanceDefault,
} from "../types";
import {
  EXPERIENCE_OPTIONS,
  INTEREST_OPTIONS,
} from "../types";
import { clearRoomBrowserData } from "./browserStorage";
import {
  deletePrivateValue,
  getPrivateValue,
  setPrivateValue,
} from "./privateStorage";

export const LOCAL_SESSION_KEY = "feel-scene:local-session:v1";
export const LOCAL_SESSION_RECORD_ID = "local-session:v1";
export const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
export const LOCAL_INDEFINITE_EXPIRY = Date.UTC(9999, 11, 31, 23, 59, 59, 999);
export const DEFAULT_ITEM_ID = CATALOGUE[0]?.id ?? "style.slow-sensual";

export const otherSeat = (seat: Seat): Seat => (seat === "A" ? "B" : "A");

function removeLegacyLocalSession() {
  try {
    localStorage.removeItem(LOCAL_SESSION_KEY);
  } catch {
    // Storage may be unavailable under a browser privacy policy.
  }
}

export function defaultEnabled(
  index: number,
  stance: Stance,
  stanceDefault: StanceDefault,
) {
  if (stanceDefault === "both") return true;
  if (stance === "switch") return true;
  const dominantIndex = stanceDefault === "dominant-first" ? 0 : 1;
  return stance === "dominant"
    ? index === dominantIndex
    : index !== dominantIndex;
}

type LegacyCatalogueItem = Omit<
  CatalogueItem,
  "framing" | "stanceDefault"
> & {
  framing: CatalogueItem["framing"] | "stance-directional";
  stanceDefault?: StanceDefault;
};

export function normalizeCatalogueItem(
  item: CatalogueItem | LegacyCatalogueItem,
): CatalogueItem {
  const framing =
    item.framing === "stance-directional" ? "directional" : item.framing;
  const stanceDefault =
    item.stanceDefault ??
    (item.framing === "stance-directional" || item.framing === "role-paired"
      ? "dominant-first"
      : "both");
  return { ...item, framing, stanceDefault };
}

export function createDraftAnswer(
  item: CatalogueItem,
  stance: Stance,
  previous?: ParticipantAnswer,
): ParticipantAnswer {
  if (previous) return normalizeParticipantAnswer(previous, item, stance);

  const perspectives = Object.fromEntries(
    item.perspectives.map((label, index) => [
      label,
      {
        enabled: defaultEnabled(index, stance, item.stanceDefault),
        interest: null,
        experience: null,
      } satisfies PerspectiveAnswer,
    ]),
  );

  return {
    itemId: item.id,
    perspectives,
    responseScaleVersion: 2,
    meaning: "",
    context: "",
    important: false,
    savedAt: null,
    updatedAt: Date.now(),
    version: 0,
    changedAfterReveal: false,
  };
}

export function normalizeParticipantAnswer(
  answer: ParticipantAnswer,
  item?: CatalogueItem,
  stance: Stance = "switch",
): ParticipantAnswer {
  if (
    answer.responseScaleVersion === 2 &&
    answer.meaning === "" &&
    !item
  ) {
    return answer;
  }
  const normalized: ParticipantAnswer = {
    ...answer,
    responseScaleVersion: 2,
    meaning: "",
    context: mergePersonalText(answer.meaning, answer.context),
    perspectives:
      answer.responseScaleVersion === 2
        ? answer.perspectives
        : Object.fromEntries(
            Object.entries(answer.perspectives).map(([label, perspective]) => {
              const legacyInterest = perspective.interest as
                | PerspectiveAnswer["interest"]
                | "curious";
              return [
                label,
                {
                  ...perspective,
                  interest:
                    legacyInterest === "want"
                      ? "yearn"
                      : legacyInterest === "curious"
                        ? "want"
                        : legacyInterest as PerspectiveAnswer["interest"],
                },
              ];
            }),
          ),
  };
  return item
    ? reconcileAnswerPerspectives(normalized, item, stance)
    : normalized;
}

export function mergePersonalText(meaning: string, context: string) {
  if (!meaning.trim()) return context;
  if (!context.trim()) return meaning;
  return `${meaning.trimEnd()}\n${context.trimStart()}`;
}

function reconcileAnswerPerspectives(
  answer: ParticipantAnswer,
  item: CatalogueItem,
  stance: Stance,
): ParticipantAnswer {
  const labels = Object.keys(answer.perspectives);
  if (
    labels.length === item.perspectives.length &&
    labels.every((label, index) => label === item.perspectives[index])
  ) {
    return answer;
  }

  let previous = Object.values(answer.perspectives);
  if (
    item.id === "group.harem" &&
    Object.hasOwn(answer.perspectives, "Surround") &&
    Object.hasOwn(answer.perspectives, "Be focal")
  ) {
    previous = [
      answer.perspectives["Be focal"],
      answer.perspectives.Surround,
    ];
  }
  if (item.perspectives.length === 1 && previous.length > 1) {
    previous = [mergeSharedPerspectives(previous)];
  }

  return {
    ...answer,
    perspectives: Object.fromEntries(
      item.perspectives.map((label, index) => [
        label,
        previous[index] ?? {
          enabled: defaultEnabled(index, stance, item.stanceDefault),
          interest: null,
          experience: null,
        },
      ]),
    ),
  };
}

function mergeSharedPerspectives(perspectives: PerspectiveAnswer[]) {
  const enabled = perspectives.filter((perspective) => perspective.enabled);
  if (!enabled.length) {
    return { enabled: false, interest: null, experience: null };
  }
  const interests = enabled.map((perspective) => perspective.interest);
  const experiences = enabled.map((perspective) => perspective.experience);
  return {
    enabled: true,
    interest: interests.some((value) => value === null)
      ? null
      : interests.reduce<ParticipantAnswer["perspectives"][string]["interest"]>(
          (lowest, value) =>
            value !== null &&
            (lowest === null ||
              INTEREST_OPTIONS.indexOf(value) > INTEREST_OPTIONS.indexOf(lowest))
              ? value
              : lowest,
          null,
        ),
    experience: experiences.some((value) => value === null)
      ? null
      : experiences.reduce<ParticipantAnswer["perspectives"][string]["experience"]>(
          (highest, value) =>
            value !== null &&
            (highest === null ||
              EXPERIENCE_OPTIONS.indexOf(value) < EXPERIENCE_OPTIONS.indexOf(highest))
              ? value
              : highest,
          null,
        ),
  };
}

export function answerIsComplete(answer: ParticipantAnswer) {
  const enabled = Object.values(answer.perspectives).filter(
    (perspective) => perspective.enabled,
  );
  if (enabled.length === 0) return true;
  return enabled.every(
    (perspective) =>
      perspective.interest !== null && perspective.experience !== null,
  );
}

export function answerHasContent(answer?: ParticipantAnswer | null) {
  if (!answer) return false;
  return (
    answer.savedAt !== null ||
    answer.meaning.trim() !== "" ||
    answer.context.trim() !== "" ||
    Object.values(answer.perspectives).some(
      (perspective) =>
        perspective.interest !== null || perspective.experience !== null,
    )
  );
}

export function createLocalSession(
  participants: Record<Seat, Participant>,
): LocalSession {
  const now = Date.now();
  return {
    mode: "local",
    roomId: crypto.randomUUID(),
    catalogueVersion: CATALOGUE_VERSION,
    createdAt: now,
    expiresAt: now + THIRTY_DAYS_MS,
    retainedIndefinitely: false,
    participants,
    activeSeat: "A",
    answers: { A: {}, B: {} },
    notes: {},
    customEntries: [],
    activeItemId: DEFAULT_ITEM_ID,
    collapsedCategories: [],
    lastUpdatedAt: now,
  };
}

let localSessionLoad: Promise<LocalSession | null> | null = null;

export function loadLocalSession(): Promise<LocalSession | null> {
  if (localSessionLoad) return localSessionLoad;
  const load = readLocalSession();
  localSessionLoad = load;
  const clear = () => {
    if (localSessionLoad === load) localSessionLoad = null;
  };
  void load.then(clear, clear);
  return load;
}

async function readLocalSession(): Promise<LocalSession | null> {
  try {
    const stored = await getPrivateValue<LocalSession>(
      LOCAL_SESSION_RECORD_ID,
      true,
    );
    if (stored) {
      const validated = await validateLocalSession(stored, true);
      if (validated) removeLegacyLocalSession();
      return validated;
    }
  } catch {
    // A legacy localStorage session can still be recovered and migrated below.
  }

  try {
    const raw = localStorage.getItem(LOCAL_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LocalSession;
    const validated = await validateLocalSession(parsed, false);
    if (!validated) return null;
    try {
      await setPrivateValue(
        LOCAL_SESSION_RECORD_ID,
        validated,
        validated.expiresAt,
      );
      removeLegacyLocalSession();
    } catch {
      // Keep a valid legacy session when private storage is unavailable.
    }
    return validated;
  } catch {
    removeLegacyLocalSession();
    return null;
  }
}

async function validateLocalSession(
  parsed: LocalSession,
  encrypted: boolean,
) {
  if (
    parsed.mode !== "local" ||
    !Number.isInteger(parsed.catalogueVersion) ||
    parsed.catalogueVersion < 1 ||
    parsed.catalogueVersion > CATALOGUE_VERSION ||
    (!parsed.retainedIndefinitely && parsed.expiresAt <= Date.now())
  ) {
    if (encrypted) {
      try {
        await deletePrivateValue(LOCAL_SESSION_RECORD_ID);
      } catch {
        // Expired storage remains inaccessible even if physical cleanup fails.
      }
    } else {
      removeLegacyLocalSession();
    }
    if (typeof parsed.roomId === "string") {
      await clearRoomBrowserData(parsed.roomId);
    }
    return null;
  }
  return {
    ...parsed,
    catalogueVersion: CATALOGUE_VERSION,
    answers: {
      A: normalizeAnswerRecord(
        parsed.answers.A,
        [...CATALOGUE, ...parsed.customEntries],
        parsed.participants.A.stance,
      ),
      B: normalizeAnswerRecord(
        parsed.answers.B,
        [...CATALOGUE, ...parsed.customEntries],
        parsed.participants.B.stance,
      ),
    },
    customEntries: parsed.customEntries.map(normalizeCatalogueItem),
  };
}

export function setLocalSessionRetention(
  session: LocalSession,
  retainedIndefinitely: boolean,
): LocalSession {
  return {
    ...session,
    retainedIndefinitely,
    expiresAt: retainedIndefinitely
      ? LOCAL_INDEFINITE_EXPIRY
      : Date.now() + THIRTY_DAYS_MS,
    lastUpdatedAt: Date.now(),
  };
}

export function normalizeAnswerRecord(
  answers: Record<string, ParticipantAnswer>,
  items: CatalogueItem[] = [],
  stance: Stance = "switch",
) {
  const itemsById = new Map(items.map((item) => [item.id, item]));
  return Object.fromEntries(
    Object.entries(answers).map(([itemId, answer]) => [
      itemId,
      normalizeParticipantAnswer(answer, itemsById.get(itemId), stance),
    ]),
  );
}

export function countSavedAnswers(
  answers: Record<string, ParticipantAnswer>,
  items: CatalogueItem[],
) {
  const itemIds = new Set(items.map((item) => item.id));
  return Object.values(answers).filter(
    (answer) => answer.savedAt !== null && itemIds.has(answer.itemId),
  ).length;
}

let queuedLocalSession: LocalSession | null = null;
let localSessionWrite: Promise<void> | null = null;

export function saveLocalSession(session: LocalSession) {
  queuedLocalSession = { ...session, lastUpdatedAt: Date.now() };
  if (!localSessionWrite) localSessionWrite = flushLocalSession();
  return localSessionWrite;
}

async function flushLocalSession() {
  try {
    while (queuedLocalSession) {
      const session = queuedLocalSession;
      queuedLocalSession = null;
      try {
        await setPrivateValue(
          LOCAL_SESSION_RECORD_ID,
          session,
          session.expiresAt,
        );
        removeLegacyLocalSession();
      } catch {
        try {
          localStorage.setItem(LOCAL_SESSION_KEY, JSON.stringify(session));
        } catch {
          // The open room remains usable in memory if all persistence is blocked.
        }
      }
    }
  } finally {
    localSessionWrite = null;
  }
}

export async function clearLocalSession() {
  queuedLocalSession = null;
  removeLegacyLocalSession();
  if (localSessionWrite) await localSessionWrite;
  try {
    await deletePrivateValue(LOCAL_SESSION_RECORD_ID);
  } catch {
    // Continue clearing any legacy session when private storage is unavailable.
  }
  removeLegacyLocalSession();
}

export function saveLocalAnswer(
  session: LocalSession,
  seat: Seat,
  answer: ParticipantAnswer,
): LocalSession {
  const previous = session.answers[seat][answer.itemId];
  const partner = session.answers[otherSeat(seat)][answer.itemId];
  const now = Date.now();
  const next: ParticipantAnswer = {
    ...answer,
    savedAt: now,
    updatedAt: now,
    version: (previous?.version ?? 0) + 1,
    changedAfterReveal:
      previous?.savedAt !== null &&
      previous?.savedAt !== undefined &&
      partner?.savedAt !== null &&
      partner?.savedAt !== undefined,
  };
  return {
    ...session,
    answers: {
      ...session.answers,
      [seat]: {
        ...session.answers[seat],
        [answer.itemId]: next,
      },
    },
    customEntries: session.customEntries.map((entry) =>
      entry.id === answer.itemId ? { ...entry, locked: true } : entry,
    ),
    lastUpdatedAt: now,
  };
}

export function importLocalAnswers(
  session: LocalSession,
  seat: Seat,
  answers: ParticipantAnswer[],
  customEntries: CatalogueItem[],
): LocalSession {
  const existingCustomIds = new Set(
    session.customEntries.map((entry) => entry.id),
  );
  let next: LocalSession = {
    ...session,
    customEntries: [
      ...session.customEntries,
      ...customEntries.filter((entry) => !existingCustomIds.has(entry.id)),
    ],
  };
  for (const answer of answers) {
    next = saveLocalAnswer(next, seat, {
      ...answer,
      version: next.answers[seat][answer.itemId]?.version ?? 0,
    });
  }
  return next;
}

export function saveLocalNote(
  session: LocalSession,
  draft: SharedNote,
): LocalSession {
  const previous = session.notes[draft.itemId];
  const note: SharedNote = {
    ...draft,
    important: Boolean(draft.important),
    updatedAt: Date.now(),
    version: (previous?.version ?? 0) + 1,
  };
  return {
    ...session,
    notes: { ...session.notes, [draft.itemId]: note },
    lastUpdatedAt: Date.now(),
  };
}

export function allItems(customEntries: CatalogueItem[]) {
  return [...CATALOGUE, ...customEntries];
}

export function daysRemaining(expiresAt: number) {
  return Math.max(0, Math.ceil((expiresAt - Date.now()) / 86_400_000));
}
